import { useDeviceType } from '@cian/ui-kit';
import * as React from 'react';
import { useSelector } from 'react-redux';

import { trackBubbleShow, trackSubsChatRichBubbleShow } from './tracking';
import { BubbleMessage } from '../../components/BubbleMessage';
import { CLIENT_INFO_BUBBLE_TYPE } from '../../constants/clientInfoBubbleType';
import { currentOpenChatIDSelector } from '../../selectors';
import { EDeviceType } from '../../types/deviceType';
import { IMessageBubble } from '../../types/messageBubble';
import { ETelemetryEvents } from '../../types/telemetry';
import { useApplicationContext } from '../../utils/applicationContext';
import { isMessageBubblesEnabled as _isMessageBubblesEnabled } from '../../utils/isMessageBubblesEnabled';
import { BubbleButtonContainer } from '../BubbleButtonContainer';
import { BubbleInputContainer } from '../BubbleInputContainer';

interface IBubbleMessageContainerProps {
  messageId: string;
  bubble: IMessageBubble;
}

export const BubbleMessageContainer: React.FC<IBubbleMessageContainerProps> = props => {
  const { bubble, messageId } = props;
  const { title, body: content, textField, buttons, analyticsBubbleType } = bubble;

  const { config, telemetry } = useApplicationContext();
  const chatId = useSelector(currentOpenChatIDSelector) as string;
  const deviceType = useDeviceType();
  const isMobile = deviceType !== EDeviceType.Desktop;

  const isClientInfoBubble = analyticsBubbleType === CLIENT_INFO_BUBBLE_TYPE;
  const isHiddenOnDesktop = isClientInfoBubble && !isMobile;
  const shouldRenderBubble = _isMessageBubblesEnabled(config) && !isHiddenOnDesktop;

  // Заголовка у баббла может не быть — маппер в таком случае кладёт пустую строку, в аналитику её не шлём
  const bubbleTitle = title.label || undefined;

  React.useEffect(() => {
    if (!shouldRenderBubble) {
      return;
    }

    trackBubbleShow(chatId, analyticsBubbleType);

    if (isClientInfoBubble) {
      trackSubsChatRichBubbleShow({
        analyticsBubbleType,
        title: bubbleTitle,
        body: content ?? null,
        buttonTitle: buttons?.[0]?.text,
      });
    }
  }, [analyticsBubbleType, bubbleTitle, buttons, chatId, content, isClientInfoBubble, shouldRenderBubble]);

  // Считаем, сколько раз баббл «Инфо о клиенте» реально дошёл до отрисовки
  React.useEffect(() => {
    if (!isClientInfoBubble || !shouldRenderBubble) {
      return;
    }

    telemetry.increment(ETelemetryEvents.ClientInfoBubbleShowSuccess);
  }, [isClientInfoBubble, shouldRenderBubble, telemetry]);

  const buttonsNode = React.useMemo(() => {
    if (!buttons?.length) {
      return null;
    }

    return buttons.map(button => (
      <BubbleButtonContainer
        key={button.text}
        messageId={messageId}
        button={button}
        analyticsBubbleType={analyticsBubbleType}
        bubbleTitle={bubbleTitle}
        bubbleBody={content}
      />
    ));
  }, [analyticsBubbleType, bubbleTitle, buttons, content, messageId]);

  if (!shouldRenderBubble) {
    return null;
  }

  return (
    <BubbleMessage
      title={title}
      content={content}
      buttonsNode={buttonsNode}
      textFieldNode={
        textField && (
          <BubbleInputContainer messageId={messageId} textField={textField} analyticsBubbleType={analyticsBubbleType} />
        )
      }
    />
  );
};
